import { trackPwaInstallConversion } from '@services/telemetry';

const STORAGE_KEY = 'saf:pwa-install-conversion';
const LEGACY_STORAGE_KEY = 'pwa_install_tracked';

type InstallConversionState = 'reported' | 'blocked';

let initialized = false;
let reporting = false;

function readState(): InstallConversionState | null {
  try {
    const state = localStorage.getItem(STORAGE_KEY);
    return state === 'reported' || state === 'blocked' ? state : null;
  } catch {
    return null;
  }
}

function writeState(state: InstallConversionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // Storage is best-effort. The signal is still never replayed during this page lifetime.
  }
}

function migrateLegacyState(): void {
  try {
    if (localStorage.getItem(LEGACY_STORAGE_KEY) === '1' && !readState()) {
      writeState('reported');
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}

async function reportInstallSignal(): Promise<void> {
  if (reporting || readState()) {
    return;
  }

  reporting = true;
  try {
    const result = await trackPwaInstallConversion();
    if (result === 'queued') {
      writeState('reported');
    } else if (result === 'denied') {
      // Remember that this historical install occurred without permission. A later preference
      // change must not replay it as if it happened under consent.
      writeState('blocked');
    }
  } finally {
    reporting = false;
  }
}

function handleInstallSignal(): void {
  void reportInstallSignal();
}

/** Registers Chromium's install signal and the iOS standalone-launch fallback exactly once. */
export function initializePwaInstallTracking(): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;
  migrateLegacyState();

  window.addEventListener('appinstalled', handleInstallSignal);
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    void reportInstallSignal();
  }
}

/** Test-only reset for module state; not used by production code. */
export function resetPwaInstallTrackingForTests(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('appinstalled', handleInstallSignal);
  }
  initialized = false;
  reporting = false;
}
